import React, { FocusEvent, useCallback, useEffect, useMemo, useRef, useState, VFC } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import { useFormContext } from 'react-hook-form'
import { Observable } from 'rxjs'

import { usePrevious } from '../../../../hooks/usePrevious'
import { useWindowKeyPress } from '../../../../hooks/useWindowKeyPress'
import { FormValues } from '../../../../lib/cart/types'
import { addressMaxLengthProp, addressMaxLengthRule } from '../../../../lib/form/address'
import { componentRestrictionsForCountry } from '../../../../lib/places/reducers'
import { AddressSuggestedPlaces, PredictedPlace } from '../AddressSuggestedPlaces'
import * as Styled from './styled'

export type PlaceResult = google.maps.places.PlaceResult

export interface AutocompleteProps {
  onPlaceSelected: (place: PlaceResult) => void
  parseSelectedPlaceError: string | undefined
  placeSelectionComplete$: Observable<void>
}

export interface AddressStreetAutocompleteFieldProps {
  autocompleteProps: AutocompleteProps
}

export const AddressStreetAutocompleteField: VFC<AddressStreetAutocompleteFieldProps> = ({
  autocompleteProps: { onPlaceSelected, placeSelectionComplete$ },
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isAddressFocused, setIsAddressFocused] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | undefined>(undefined)
  const [isListenChanges, setIsListenChanges] = useState<boolean>(true)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
  })

  const { watch } = useFormContext<FormValues.Address>()
  const street1 = watch('street1')
  const countryCode = watch('country_code')

  const escapePressed = useWindowKeyPress({ targetKey: 'Escape' })
  const previousEscapePressed = usePrevious(escapePressed)

  const shouldDisplayPredictions =
    isAddressFocused && placePredictions.length > 0 && !!street1 && !selectedPlace
  const isDisabled = isLoadingDetails && shouldDisplayPredictions

  const componentRestrictions = useMemo(
    () => (countryCode ? componentRestrictionsForCountry(countryCode) : undefined),
    [countryCode],
  )

  const onPlaceSelection = useCallback(
    (place: PredictedPlace) => {
      if (!placesService || isDisabled) {
        return
      }
      setIsListenChanges(false)
      setIsLoadingDetails(true)
      placesService.getDetails({ placeId: place.place_id }, (details, status) => {
        setIsLoadingDetails(false)
        if (status === 'OK' && details) {
          setSelectedPlace(details)
        } else {
          setSelectedPlace(undefined)
        }
      })
    },
    [isDisabled, placesService],
  )

  const onFocus = useCallback(() => {
    setIsAddressFocused(true)
  }, [])

  const onBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    if (
      event.relatedTarget &&
      containerRef.current &&
      containerRef.current.contains(event.relatedTarget)
    ) {
      return
    }
    setIsAddressFocused(false)
  }, [])

  useEffect(() => {
    const subscription = placeSelectionComplete$.subscribe(() => {
      setIsListenChanges(true)
    })
    return () => subscription.unsubscribe()
  }, [placeSelectionComplete$])

  useEffect(() => {
    if (escapePressed) {
      setIsListenChanges(false)
      setIsAddressFocused(false)
    }
  }, [escapePressed])

  useEffect(() => {
    if (!escapePressed && previousEscapePressed) {
      setIsListenChanges(true)
    }
  }, [escapePressed, previousEscapePressed])

  useEffect(
    () => {
      if (!isListenChanges) {
        return
      }
      setSelectedPlace(undefined)
      setIsLoadingDetails(false)
      if (!street1) {
        return
      }
      getPlacePredictions({ input: street1, componentRestrictions: componentRestrictions })
    },
    // getPlacesPredictions changes every time it's called triggering useEffect loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [street1],
  )

  useEffect(() => {
    if (!selectedPlace || isLoadingDetails) {
      return
    }
    onPlaceSelected(selectedPlace)
  }, [isLoadingDetails, onPlaceSelected, selectedPlace])

  return (
    <Styled.Container
      ref={containerRef}
      // onBlur uses focusout which bubbles up to the container
      // - catch here (instead of input) to potentially save some work
      onBlur={onBlur}
    >
      <Styled.AddressStreetInputField
        $predictionsCount={shouldDisplayPredictions ? placePredictions.length : 0}
        fsExclude
        id="street1"
        name="street1"
        label="Address"
        disabled={isDisabled}
        autoComplete="address-line1"
        onFocus={onFocus}
        inputProps={addressMaxLengthProp}
        optionalRules={addressMaxLengthRule}
        onKeyDown={(e) => {
          // prevent form submission when selecting autocomplete address
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        data-cy="address1"
      />
      {shouldDisplayPredictions && (
        <AddressSuggestedPlaces
          suggestedPlaces={placePredictions}
          isDisabled={isDisabled}
          onPlaceSelection={onPlaceSelection}
        />
      )}
    </Styled.Container>
  )
}
